import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'sonner';

const EMPTY_FORM = {
  name: '',
  year: new Date().getFullYear(),
  start_date: '',
  end_date: '',
  details: '',
  location_type: 'local',
  location: '',
  tournament_style: 'competition',
  age_categories: [],
  registration_start_date: '',
  registration_end_date: '',
  initial_payment_amount: 0,
  age_cutoff_date: '',
  age_validation_method: 'auto_cutoff',
  age_category_date_ranges: [],
  target_program_ids: [],
  is_division_based: false,
  divisions: [],
  allow_play_up: true,
};

/**
 * iter270 — Self-contained hook that powers the Admin "Edit Tournament"
 * dialog from anywhere (e.g. the Tournaments list page AND the
 * TournamentDetail page). Wires in the iter240 fee-change cascade
 * preview so we never bypass the refund/charge confirmation flow.
 *
 * Usage:
 *   const edit = useTournamentEditDialog({ onSaved: () => refetch() });
 *   <Button onClick={() => edit.openEditDialog(tournament)}>Edit</Button>
 *   <AdminTournamentFormDialog {...edit.dialogProps} />
 *   {edit.amountChangeNode}
 */
export default function useTournamentEditDialog({ onSaved } = {}) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [, setUploadingImage] = useState(false);
  const [programs, setPrograms] = useState([]);

  // iter240 cascade preview state.
  const [amountChangePreview, setAmountChangePreview] = useState(null);
  const [amountChangeOpen, setAmountChangeOpen] = useState(false);
  const [amountChangeSubmitting, setAmountChangeSubmitting] = useState(false);

  // Programs are needed by the form for "Target Program" multi-select.
  useEffect(() => {
    api.get('/programs').then((r) => setPrograms(r.data || [])).catch(() => setPrograms([]));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({ ...EMPTY_FORM, year: new Date().getFullYear() });
    setSelectedTournament(null);
    setImageFile(null);
    setImagePreview(null);
  }, []);

  const openEditDialog = useCallback((tournament) => {
    if (!tournament) return;
    setSelectedTournament(tournament);
    setFormData({
      name: tournament.name || '',
      year: tournament.year,
      start_date: tournament.start_date || '',
      end_date: tournament.end_date || '',
      details: tournament.details || '',
      location_type: tournament.location_type || 'local',
      location: tournament.location || '',
      tournament_style: tournament.tournament_style || 'competition',
      age_categories: tournament.age_categories || [],
      registration_start_date: tournament.registration_start_date || '',
      registration_end_date: tournament.registration_end_date || '',
      initial_payment_amount: tournament.initial_payment_amount || 0,
      age_cutoff_date: tournament.age_cutoff_date || '',
      age_validation_method: tournament.age_validation_method || 'auto_cutoff',
      age_category_date_ranges: tournament.age_category_date_ranges || [],
      target_program_ids: tournament.target_program_ids || [],
      is_division_based: tournament.is_division_based || false,
      divisions: tournament.divisions || [],
      allow_play_up: tournament.allow_play_up !== false,
    });
    if (tournament.image_path) {
      setImagePreview(`${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/files/tournament-image/${tournament.id}`);
    } else {
      setImagePreview(null);
    }
    setImageFile(null);
    setShowEditDialog(true);
  }, []);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = async () => {
    if (showEditDialog && selectedTournament?.image_path) {
      try {
        await api.delete(`/files/tournament-image/${selectedTournament.id}`);
        toast.success('Image removed');
      } catch {
        toast.error('Failed to remove image');
        return;
      }
    }
    setImageFile(null);
    setImagePreview(null);
  };

  const uploadTournamentImage = async (tournamentId) => {
    if (!imageFile) return;
    try {
      setUploadingImage(true);
      const fd = new FormData();
      fd.append('file', imageFile);
      await api.post(`/files/upload/tournament-image?tournament_id=${tournamentId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Tournament image uploaded');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const doActualUpdate = async () => {
    try {
      await api.put(`/tournaments/${selectedTournament.id}`, formData);
      if (imageFile) await uploadTournamentImage(selectedTournament.id);
      toast.success('Tournament updated successfully');
      setShowEditDialog(false);
      resetForm();
      onSaved && onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tournament');
    }
  };

  // Public submit — checks for fee changes and routes through the cascade
  // preview when needed (iter240).
  const handleUpdate = async () => {
    const oldAmount = Number(selectedTournament?.initial_payment_amount || 0);
    const newAmount = Number(formData?.initial_payment_amount || 0);
    const amountChanged = Math.abs(oldAmount - newAmount) > 0.005;
    if (amountChanged) {
      try {
        const { data: preview } = await api.post(
          `/tournaments/${selectedTournament.id}/preview-payment-amount-change`,
          { new_amount: newAmount },
        );
        if (preview.total_registrations_affected > 0) {
          setAmountChangePreview(preview);
          setAmountChangeOpen(true);
          return;
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to preview fee change.');
        return;
      }
    }
    await doActualUpdate();
  };

  const confirmAmountChange = async () => {
    try {
      setAmountChangeSubmitting(true);
      const newAmount = Number(formData?.initial_payment_amount || 0);
      const { data: result } = await api.post(
        `/tournaments/${selectedTournament.id}/apply-payment-amount-change`,
        { new_amount: newAmount },
      );
      const refunds = result?.counts?.refund ?? 0;
      const charges = result?.counts?.additional_charge ?? 0;
      const updates = result?.counts?.invoice_update ?? 0;
      const errCount = result?.errors?.length ?? 0;
      if (errCount > 0) {
        toast.warning(`Fee change applied with ${errCount} error(s).`, { duration: 8000 });
      } else {
        toast.success(
          `Fee change applied — ${refunds} refund(s), ${charges} new invoice(s), ${updates} invoice update(s).`,
          { duration: 6000 },
        );
      }
      setAmountChangeOpen(false);
      setAmountChangePreview(null);
      await doActualUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply fee change.');
    } finally {
      setAmountChangeSubmitting(false);
    }
  };

  const toggleAgeCategory = (cat) => {
    setFormData((prev) => ({
      ...prev,
      age_categories: prev.age_categories.includes(cat)
        ? prev.age_categories.filter((c) => c !== cat)
        : [...prev.age_categories, cat],
    }));
  };

  const toggleTargetProgram = (programId) => {
    setFormData((prev) => ({
      ...prev,
      target_program_ids: prev.target_program_ids.includes(programId)
        ? prev.target_program_ids.filter((id) => id !== programId)
        : [...prev.target_program_ids, programId],
    }));
  };

  // Props bundle for direct spread onto AdminTournamentFormDialog.
  const dialogProps = {
    showCreateDialog: false,
    setShowCreateDialog: () => {},
    showEditDialog,
    setShowEditDialog,
    formData,
    setFormData,
    imagePreview,
    removeImage,
    handleImageSelect,
    resetForm,
    handleCreate: () => {},
    handleUpdate,
    programs,
    toggleAgeCategory,
    toggleTargetProgram,
  };

  return {
    openEditDialog,
    dialogProps,
    // Caller renders the amount-change confirmation dialog themselves.
    amountChange: {
      open: amountChangeOpen,
      setOpen: setAmountChangeOpen,
      preview: amountChangePreview,
      onConfirm: confirmAmountChange,
      submitting: amountChangeSubmitting,
    },
  };
}
